import { useState, useEffect } from 'react';

import { MtxButton, MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import IntlTelInput from 'intl-tel-input/reactWithUtils';
import 'intl-tel-input/styles';
import { useTranslation } from 'react-i18next';

import FormTextError from '@/components/formTextError/formTextError';
import { useTimeZoneFormatter } from '@/hooks/useTimeZoneFormatter';
import { Day, Plage } from '@/services/types/Rappel';

import styles from './styles.module.css';

type RappelConseillerFormProps = {
  data: Day[];
  initialPhone?: string;
  initialPhoneValid?: boolean;
  onSubmit: (formData: FormData) => void;
  isLoading?: boolean;
  scrollAnchorId?: string;
};

export const AdvisorCallbackForm: React.FC<RappelConseillerFormProps> = ({
  data,
  initialPhone = '',
  initialPhoneValid = false,
  onSubmit,
  isLoading = false,
  scrollAnchorId = 'rappel-form-anchor',
}) => {
  const { t } = useTranslation();
  const { formatTimeRange } = useTimeZoneFormatter();

  const [phone, setPhone] = useState(initialPhone);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedPlage, setSelectedPlage] = useState<string>('');
  const [objetRappel, setObjetRappel] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(initialPhoneValid);
  const [phoneTextError, setPhoneTextError] = useState('');

  const selectedDay = data?.find((day: Day) => day.date === selectedDate);

  const scrollToInputError = () => {
    const anchor = document.getElementById(scrollAnchorId);
    anchor?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkFormError = () => {
    setPhoneTextError('');

    if (selectedDate === '' || selectedPlage === '') {
      scrollToInputError();
      return true;
    }

    if (phone === '') {
      setPhoneTextError(t('helpContact.fieldRequired'));
      scrollToInputError();
      return true;
    } else if (!isPhoneValid) {
      setPhoneTextError(t('helpContact.invalidPhone'));
      scrollToInputError();
      return true;
    }

    return false;
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);

    const newDay = data.find((day) => day.date === newDate);
    if (newDay && newDay.plages.length > 0) {
      setSelectedPlage(newDay.plages[0].slug);
    } else {
      setSelectedPlage('');
    }
  };

  const handlePlageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPlage(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);

    if (checkFormError()) return;

    const formData = new FormData();
    formData.append('numeroRappel', phone);
    formData.append('jourRappel', selectedDate);
    formData.append('heureRappel', selectedPlage);
    formData.append('objetRappel', objetRappel);

    onSubmit(formData);
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setSelectedDate(data[0].date);
      if (data[0].plages.length > 0) {
        setSelectedPlage(data[0].plages[0].slug);
      }
    }
  }, [data]);

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div>
        <div className="scroll-anchor" id={scrollAnchorId} />
        <label className={`${styles.formLabel} ${styles.requiredLabel}`} htmlFor="phone-input">
          {t('helpContact.label')}
        </label>
        <IntlTelInput
          initialCountry="fr"
          separateDialCode={false}
          showFlags={true}
          strictMode={true}
          formatAsYouType={true}
          formatOnDisplay={true}
          fixDropdownWidth={true}
          nationalMode={false}
          onChangeNumber={(value) => {
            if (value !== phone) setSubmitted(false);
            setPhone(value);
          }}
          onChangeValidity={(isValid) => setIsPhoneValid(isValid)}
          inputProps={{
            id: 'phone-input',
            className: styles.phoneInputContainer + (submitted && phoneTextError !== '' ? ` ${styles.inputError}` : ''),
            name: 'phone',
            placeholder: '06 00 00 00 00',
            required: true,
            defaultValue: initialPhone || undefined,
          }}
        />
        {submitted && phoneTextError !== '' && <FormTextError text={phoneTextError} />}
      </div>

      <div>
        <label className={`${styles.formLabel} ${styles.requiredLabel}`} htmlFor="jourRappel">
          {t('helpContact.callbackTimeLabel')}
        </label>
        <div className={styles.timeSlotContainer}>
          <div className={styles.selectWrapper}>
            <select id="jourRappel" className={styles.selectInput} onChange={handleDateChange} value={selectedDate}>
              {data?.map((day: Day) => (
                <option key={day.date} value={day.date}>
                  {day?.dateEcrite}
                </option>
              ))}
            </select>
            <MtxIcon className={styles.selectIcon} name="chevron-down-big" size={24} />
            {submitted && selectedDate === '' && <FormTextError text={t('helpContact.fieldRequired')} />}
          </div>

          <div className={styles.selectWrapper}>
            <select
              id="plageRappel"
              className={styles.selectInput}
              onChange={handlePlageChange}
              value={selectedPlage}
              aria-label={t('helpContact.callbackTimeslotLabel')}
            >
              {selectedDay?.plages.map((plage: Plage) => (
                <option key={plage.slug} value={plage.slug}>
                  {formatTimeRange(plage)}
                </option>
              ))}
            </select>
            <MtxIcon className={styles.selectIcon} name="chevron-down-big" size={24} />
            {submitted && selectedPlage === '' && <FormTextError text={t('helpContact.fieldRequired')} />}
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="recall-input-reason" className={styles.formLabel}>
          {t('helpContact.callbackReasonLabel')}
        </label>
        <textarea
          id="recall-input-reason"
          className={styles.formInput}
          placeholder={t('helpContact.callbackReasonPlaceholder')}
          onChange={(e) => setObjetRappel(e.target.value)}
        />
      </div>

      <div className={styles.bottomInfo}>
        <p>
          {t('helpContact.phoneContactMessage')} <span className={styles.bolderText}>{t('helpContact.tel')}</span>{' '}
          {t('helpContact.callCostMessage')}
        </p>
        <p>{t('helpContact.availabilitySchedule')}</p>
      </div>

      <div className={styles.btnWrapper}>
        <MtxButton onClick={handleSubmit} variant="primary" size="large" full-width="true" disabled={isLoading}>
          <div>{t('helpContact.buttonText')}</div>
        </MtxButton>
      </div>
    </form>
  );
};
